import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { CvsiInternalClient, staffCan, type StaffWhoami } from '@cvsi/shared';
import { loadSession, saveSession, clearSession, type StaffSession } from './auth';

interface StaffContextValue {
  session: StaffSession | null;
  client: CvsiInternalClient | null;
  /** The signed-in operator's resolved role/areas (from /internal/whoami); null until loaded. */
  whoami: StaffWhoami | null;
  signIn: (s: StaffSession) => void;
  signOut: () => void;
}

const Ctx = createContext<StaffContextValue | null>(null);

export function StaffProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<StaffSession | null>(() => loadSession());
  const [whoami, setWhoami] = useState<StaffWhoami | null>(null);

  const signIn = useCallback((s: StaffSession) => { saveSession(s); setSession(s); }, []);
  const signOut = useCallback(() => { clearSession(); setSession(null); setWhoami(null); }, []);

  const client = useMemo(() => {
    if (!session) return null;
    return new CvsiInternalClient({ baseUrl: session.baseUrl, actorId: session.actorId, tokenProvider: async () => session.token });
  }, [session]);

  // Resolve the operator's real role/areas so the UI mirrors backend enforcement.
  useEffect(() => {
    if (!client) { setWhoami(null); return; }
    let cancelled = false;
    client.whoami().then((w) => { if (!cancelled) setWhoami(w); }).catch(() => { if (!cancelled) setWhoami(null); });
    return () => { cancelled = true; };
  }, [client]);

  const value = useMemo(() => ({ session, client, whoami, signIn, signOut }), [session, client, whoami, signIn, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStaff(): StaffContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useStaff must be used within <StaffProvider>');
  return v;
}

export function useClient(): CvsiInternalClient {
  const { client } = useStaff();
  if (!client) throw new Error('No client — route is not authenticated');
  return client;
}

/** True when the signed-in operator's role grants `area`. Used to gate destructive UI controls. */
export function useArea(area: string): boolean {
  const { whoami } = useStaff();
  return staffCan(whoami?.areas, area);
}
